import { BARRACKS_LEVELS, BLD_COSTS, BLD_INFO, DIFFICULTY_INFO, GATE_LEVEL_NAMES, MAX_BARRACKS_LVL, MAX_TOWER_LVL, MAX_WALL_LVL, RICHNESS_INFO, TOWER_LEVELS, UNIT_INFO, WALL_LEVELS } from '../network/protocol';
import type { Building, PlayerState, ResNode, RoomInfo, Snapshot, Unit } from '../network/protocol';
import type { ConnStatus } from '../network/socket';
import { sfx } from '../audio/sound';

export interface HudCallbacks {
  getName(): string;
  setName(n: string): void;
  onCreate(bots: number, diff: string, rich: string): void;
  onSetDiff(diff: string): void;
  onSetRich(rich: string): void;
  onJoin(room: string): void;
  onRefreshRooms(): void;
  onRetry(): void;
  onAddBot(): void;
  onStart(): void;
  onLeave(): void;
  onTrain(kind: string): void;
  onUpgrade(): void;
  onConvert(): void;
  onGatherNode(): void;
  onSelectVillagers(scope: string): void;
  onSelectArmy(kind: string): void;
  onPlace(kind: string | null): void;
  onStop(): void;
  onAmove(): void;
  onToggleHelp(): void;
  onToggleFullscreen(): void;
}

export interface ArmyGroup {
  kind: string;
  n: number;
}

export interface VillageOverview {
  villagers: number;
  idle: number;
  army: ArmyGroup[];
  armyTotal: number;
}

export interface LiveCost {
  btn: HTMLElement;
  cost: HTMLElement;
  needs: { w: number; g: number; f: number };
  okLabel: string;
}

export interface PanelCtx {
  units: Unit[];
  bld: Building | null;
  me: PlayerState | null;
  placement: string | null;
  amoveArmed: boolean;
  enemy: Building | Unit | null;
  node: ResNode | null;
  overview: VillageOverview | null;
  snap: Snapshot | null;
}

function el(tag: string, cls = '', html = ''): HTMLElement {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (html) e.innerHTML = html;
  return e;
}

// Player names come from other clients — never inject them raw.
function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// Occupancy dots: ●●●○○○
function seatDots(filled: number, total: number): string {
  let s = '';
  for (let i = 0; i < total; i++) {
    s += `<span class="${i < filled ? 'text-amber-400' : 'text-white/15'}">●</span>`;
  }
  return `<span class="text-[10px] leading-none tracking-[0.15em]">${s}</span>`;
}

// HUD builds all DOM overlay: lobby screens, top resource bar, bottom
// command bar, toasts, and help. Game logic lives in game.ts.
export class HUD {
  root: HTMLElement;
  private top: HTMLElement;
  private bottom: HTMLElement;
  private lobby: HTMLElement;
  private toasts: HTMLElement;
  private over: HTMLElement;
  private help: HTMLElement;
  private hint: HTMLElement;
  private cb: HudCallbacks;
  minimapSlot: HTMLElement;
  private barCenter: HTMLElement;
  private barRight: HTMLElement;
  private barInfo: HTMLElement;
  private barCmds: HTMLElement;
  // DOM-stability cache: the bar re-renders 4×/sec, which drops :hover for
  // a frame on every swapped button (the flicker). Command/affordability
  // buttons are only rebuilt when their definition changes; ticking numbers
  // update in place instead.
  private lastCmdHTML = '';
  private lastOvHTML = '';
  private lastTopHTML = '';
  private liveCosts: LiveCost[] = [];
  private lastToast = 0;
  private fsBtn: HTMLElement;
  private muteBtn: HTMLElement;

  constructor(parent: HTMLElement, cb: HudCallbacks) {
    this.cb = cb;
    this.root = el('div', 'absolute inset-0 pointer-events-none select-none');
    parent.appendChild(this.root);

    // Home badge (top-left, like the other games): back to the etoo hub.
    const home = el(
      'a',
      'absolute top-2 left-2 z-40 hud-panel px-2 py-1 text-white text-sm pointer-events-auto flex items-center gap-1.5 no-underline',
      `<svg width="22" height="22" viewBox="0 0 32 32" aria-hidden="true">` +
        `<defs><linearGradient id="etoo-mark-bg-bharat" x1="0" y1="0" x2="1" y2="1">` +
        `<stop offset="0%" stop-color="#8b5cf6"/><stop offset="100%" stop-color="#a78bfa"/>` +
        `</linearGradient></defs>` +
        `<rect width="32" height="32" rx="7.5" fill="url(#etoo-mark-bg-bharat)"/>` +
        `<g fill="none" stroke="#ffffff" stroke-width="2.4" stroke-linecap="round">` +
        `<path d="M22.6 14 C22.1 11.7 19.5 10.5 16.2 10.5 C12.8 10.5 10.7 13 10.7 16.5 C10.7 20 12.8 22.5 16.2 22.5 C19.5 22.5 22.1 21.3 22.6 19"/>` +
        `<line x1="11.5" y1="16.5" x2="24.2" y2="16.5"/>` +
        `</g></svg><span class="font-bold tracking-tight">etoo</span>`,
    );
    home.setAttribute('href', '/');
    (home as HTMLAnchorElement).title = 'etoo — all games';
    home.setAttribute('aria-label', 'etoo home');
    this.root.appendChild(home);

    // Persistent fullscreen toggle (top-right, visible in lobby and in game).
    this.fsBtn = el(
      'button',
      'absolute top-2 right-2 hud-panel px-2.5 py-1.5 text-white text-sm pointer-events-auto',
      '⛶',
    );
    this.fsBtn.title = 'Fullscreen (F)';
    this.fsBtn.addEventListener('click', () => this.cb.onToggleFullscreen());
    const syncFsIcon = () => {
      this.fsBtn.innerHTML = document.fullscreenElement ? '🗗' : '⛶';
    };
    document.addEventListener('fullscreenchange', syncFsIcon);
    syncFsIcon();
    this.root.appendChild(this.fsBtn);

    // Persistent mute toggle next to it.
    this.muteBtn = el(
      'button',
      'absolute top-2 right-14 hud-panel px-2.5 py-1.5 text-white text-sm pointer-events-auto',
      '',
    );
    this.muteBtn.title = 'Sound on/off (M)';
    this.muteBtn.addEventListener('click', () => {
      sfx.toggleMute();
      this.syncMuteIcon();
    });
    this.syncMuteIcon();
    this.root.appendChild(this.muteBtn);

    // Every UI button gets a click sound (game orders play their own).
    this.root.addEventListener('click', (e) => {
      const t = e.target as HTMLElement | null;
      if (t && t.closest('button:not([disabled])')) sfx.click();
    });

    this.top = el(
      'div',
      'absolute top-2 left-1/2 -translate-x-1/2 hidden items-center gap-3 px-4 py-1.5 hud-panel text-sm text-white pointer-events-auto',
    );
    this.root.appendChild(this.top);

    // Full-width AoE-style command bar: selection + actions center, village
    // overview right. Cells persist; setPanel only rewrites their contents.
    // The minimap floats bottom-right above the bar so the strip stays slim.
    this.bottom = el(
      'div',
      'absolute bottom-0 left-0 right-0 hidden pointer-events-auto aobar',
    );
    const barInner = el('div', 'flex items-stretch justify-between gap-3 px-3 py-1 w-full');
    this.barCenter = el('div', 'flex-1 flex flex-col items-center justify-center gap-1 min-w-0');
    this.barRight = el('div', 'flex flex-col justify-center gap-1 shrink-0 w-64 border-l border-amber-200/15 pl-3');
    // Persistent cells: info/cmds contents are rewritten, never the cells.
    this.barInfo = el('div', 'px-3 py-1 text-xs text-gray-200 text-center');
    this.barCmds = el('div', 'flex gap-1.5 justify-center flex-wrap');
    this.barCenter.append(this.barInfo, this.barCmds);
    barInner.append(this.barCenter, this.barRight);
    this.bottom.appendChild(barInner);
    this.root.appendChild(this.bottom);

    const mmWrap = el(
      'div',
      'absolute right-2 hud-panel p-1.5 pointer-events-auto hidden flex-col items-center gap-1',
    );
    mmWrap.style.bottom = '118px';
    this.minimapSlot = el('div', 'flex items-center rounded-lg border border-amber-200/25 bg-black/50 p-1 shadow-[0_4px_16px_rgba(0,0,0,0.5)]');
    mmWrap.appendChild(this.minimapSlot);
    this.root.appendChild(mmWrap);
    mmWrap.id = 'mmwrap';

    // Delegated clicks: the bar + top strip re-render up to 4×/sec, which
    // can swap a button between press and release. One persistent listener
    // per zone routes via data-act, so clicks always land no matter how
    // fresh the button under the cursor is. Disabled buttons are ignored.
    const route = (e: Event) => {
      const t = e.target as HTMLElement | null;
      const b = t?.closest?.('[data-act]') as HTMLElement | null;
      if (!b || b.closest('[disabled]')) return;
      const act = b.getAttribute('data-act') ?? '';
      const arg = b.getAttribute('data-arg') ?? '';
      switch (act) {
        case 'train': this.cb.onTrain(arg); break;
        case 'upgrade': this.cb.onUpgrade(); break;
        case 'convert': this.cb.onConvert(); break;
        case 'gather-node': this.cb.onGatherNode(); break;
        case 'place': this.cb.onPlace(arg === '' ? null : arg); break;
        case 'stop': this.cb.onStop(); break;
        case 'amove': this.cb.onAmove(); break;
        case 'sel-vill': this.cb.onSelectVillagers(arg); break;
        case 'sel-army': this.cb.onSelectArmy(arg); break;
        case 'help': this.cb.onToggleHelp(); break;
        case 'leave': this.cb.onLeave(); break;
      }
    };
    this.barCenter.addEventListener('click', route);
    this.barRight.addEventListener('click', route);
    this.top.addEventListener('click', route);

    this.hint = el(
      'div',
      'absolute top-14 left-1/2 -translate-x-1/2 hidden px-3 py-1 hud-panel text-xs text-amber-200',
    );
    this.root.appendChild(this.hint);

    this.toasts = el(
      'div',
      'absolute top-24 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1',
    );
    this.root.appendChild(this.toasts);

    this.lobby = el('div', 'absolute inset-0 pointer-events-auto overflow-y-auto');
    this.root.appendChild(this.lobby);

    this.over = el('div', 'absolute inset-0 hidden items-center justify-center pointer-events-auto bg-black/60');
    this.root.appendChild(this.over);

    this.help = el('div', 'absolute inset-0 hidden items-center justify-center pointer-events-auto bg-black/60');
    this.help.innerHTML = `
      <div class="hud-panel max-w-md p-5 text-sm text-gray-200">
        <h2 class="text-lg font-bold text-white mb-2">How to play</h2>
        <ul class="list-disc pl-5 space-y-1">
          <li><b>Left-click / drag</b>: select villagers & soldiers. <b>Double-click</b>: select all of that type on screen. <b>Left-click</b> a 🌳 tree, ⛏️ mine or 🫐 bush to inspect it — the bottom bar shows what is left and can send nearby idle villagers to gather.</li>
          <li>The <b>bottom bar</b> always shows your village at a glance: 🧑‍🌾 villager count (+ idle) with <b>All / Idle / +5</b> quick-select, and ⚔️ army counts with per-kind select. <b>.</b> selects idle villagers anywhere.</li>
          <li><b>Right-click</b>: move · gather from 🪵💰🍎 · attack enemies. Hover a resource with villagers selected to preview the task; every order lands with a colored ping.</li>
          <li><b>A</b> then click: attack-move. <b>S</b>: stop. <b>Esc</b>: cancel placement.</li>
          <li>Villagers gather <b>🪵 wood</b> (trees), <b>💰 gold</b> (mines), <b>🍎 food</b> (bushes) and raise buildings.</li>
          <li><b>H</b> house (+5 pop) · <b>G</b> farm (endless food) · <b>R</b> barracks · <b>T</b> tower · <b>Y</b> wall — a ghost follows your cursor (green = buildable, red = blocked; the tower also shows its firing range), then click the map. Shift-click places several. Walls work best as a drag: click where the rampart starts, drag to the end, release — selected villagers split across the foundations and raise the whole line at once. Wall segments snap edge-to-edge into sealed ramparts that block movement — batter through with swords and arrows.</li>
          <li>Builders pulled away stall the foundation — <b>right-click it with villagers</b> to resume (free, already paid). A villager that finishes a building walks to the next unfinished foundation nearby on its own, so one worker raises a whole dragged wall line.</li>
          <li>Damaged buildings can be <b>repaired</b>: right-click them with villagers (8 HP/s each, billed in 🪵).</li>
          <li>Select a finished <b>🗼 tower</b> (or press <b>U</b>) to upgrade it: 🔫 Gunner → 🚀 Rocketeer → 💣 Siege. Each tier hits harder, shoots farther, and adds HP. Research takes 20–40s and the tower keeps firing meanwhile.</li>
          <li>Select a finished <b>⚒️ barracks</b> (or press <b>U</b>) to upgrade it: 🔱 Armory unlocks <b>spearmen [3]</b> (long reach) → 🛡️ Royal Akhara unlocks <b>rajputs [4]</b> (elite heavy infantry). Research takes 30–40s and training continues meanwhile.</li>
          <li>Walls start as wooden <b>🪵 palisades</b> — select a finished wall (or press <b>U</b>) to upgrade it to a <b>🧱 Stone Wall</b> (+450 HP, 50🪵 20💰, 15s). Stone walls are taller, crenellated, and far harder to batter down.</li>
          <li>Select a finished wall and press <b>🛡️ Gate [G]</b> (30🪵, instant) to swing it into a gate: your villagers and soldiers stroll through as the leaves open for them, while enemies face a sealed wall they must batter down. Gates line up with neighbouring walls automatically and upgrade to stone like any wall.</li>
          <li>Wild 🍎 bushes run out — build a <b>🌾 farm</b> (60🪵) for endless crops, then right-click it with villagers. Every unit now eats food: 🧑‍🌾 50🍎 · ⚔️ 60🍎 25💰 · 🏹 25🍎 45🪵 40💰 · 🔱 50🍎 20🪵 · 🛡️ 90🍎 60💰.</li>
          <li>Your <b>Town Center (🏛️)</b> is your capital: only it trains villagers, and losing it eliminates you on the spot — no matter how big your army is. Raze theirs to win.</li>
          <li>Raze the enemy <b>Town Center</b> to win — and guard yours. Push the cursor to a screen edge (AoE style), use arrows / middle-drag / minimap to move the camera, wheel zooms, Q/E rotate. <b>F</b> or the ⛶ button toggles fullscreen. Sound effects play throughout — <b>M</b> or 🔊 mutes.</li>
        </ul>
        <button id="bharat-help-close" class="cmd-btn mt-3 mx-auto">Close</button>
      </div>`;
    this.root.appendChild(this.help);
    this.help.querySelector('#bharat-help-close')!.addEventListener('click', () => cb.onToggleHelp());
  }

  private show(e: HTMLElement, flex = false): void {
    e.classList.remove('hidden');
    if (flex) e.classList.add('flex');
  }
  private hide(e: HTMLElement): void {
    e.classList.add('hidden');
    e.classList.remove('flex');
  }
  isHelpOpen(): boolean {
    return !this.help.classList.contains('hidden');
  }
  syncMuteIcon(): void {
    this.muteBtn.innerHTML = sfx.muted ? '🔇' : '🔊';
  }
  toggleHelp(): void {
    if (this.isHelpOpen()) this.hide(this.help);
    else this.show(this.help, true);
  }

  // ---- lobby ----

  showMenu(rooms: RoomInfo[], status: ConnStatus = 'open'): void {
    this.hide(this.top);
    this.hide(this.bottom);
    document.getElementById('mmwrap')?.classList.add('hidden');
    this.hide(this.over);
    const L = this.lobby;
    L.innerHTML = '';
    L.className = 'absolute inset-0 pointer-events-auto overflow-y-auto';
    this.show(L);
    const wrap = el(
      'div',
      'min-h-full flex items-center justify-center p-4 bg-[#070b18] relative overflow-y-auto',
    );
    wrap.innerHTML = `
      <div class="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-orange-600/20 blur-3xl pointer-events-none"></div>
      <div class="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-indigo-600/20 blur-3xl pointer-events-none"></div>`;
    const card = el(
      'div',
      'relative w-full max-w-xl rounded-2xl border border-amber-200/15 bg-[#0d1428]/90 shadow-[0_20px_80px_-15px_rgba(0,0,0,0.9)] backdrop-blur p-6 sm:p-8 text-gray-200 my-6',
    );
    const statusBanner =
      status === 'open'
        ? ''
        : status === 'connecting'
          ? `<div class="mt-4 flex items-center gap-2 rounded-xl border border-sky-400/25 bg-sky-400/10 px-3 py-2.5 text-xs text-sky-200"><span class="animate-pulse">🔌</span> Connecting to server…</div>`
          : `<div class="mt-4 rounded-xl border border-red-400/25 bg-red-400/10 px-3 py-2.5 text-xs text-red-200">
               ⚠️ Cannot reach the game server. Start it with <code>go run main.go</code> (dev) or check <code>/bharat/ws</code> (docker).
               <button id="bharat-retry" class="ml-2 rounded-lg bg-red-400/20 hover:bg-red-400/30 px-2.5 py-1 font-bold">↻ Retry</button>
             </div>`;
    card.innerHTML = `
      <div class="text-center">
        <div class="text-4xl">🏛️</div>
        <h1 class="mt-1 text-4xl sm:text-5xl font-black tracking-tight bg-gradient-to-b from-amber-100 via-amber-300 to-orange-500 bg-clip-text text-transparent">Cities of Bharat</h1>
        <p class="mt-2 text-sm text-gray-400">Command villagers, raise armies, and raze capitals in a 3D real-time strategy war.</p>
        <div class="mt-3 flex flex-wrap justify-center gap-1.5 text-[11px] text-gray-300">
          <span class="rounded-full border border-white/10 bg-white/5 px-2.5 py-1">⚔️ Real-time battles</span>
          <span class="rounded-full border border-white/10 bg-white/5 px-2.5 py-1">🏠 Rooms of 6</span>
          <span class="rounded-full border border-white/10 bg-white/5 px-2.5 py-1">🤖 Raja Bots</span>
          <span class="rounded-full border border-white/10 bg-white/5 px-2.5 py-1">👑 Sudden-death capitals</span>
        </div>
      </div>
      ${statusBanner}
      <div class="mt-5 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
        <label class="block text-[11px] font-bold uppercase tracking-[0.2em] text-gray-400">Your battle name</label>
        <input id="bharat-name" maxlength="16" placeholder="e.g. Ashoka"
          class="mt-1.5 w-full rounded-xl border border-white/15 bg-black/30 px-4 py-2.5 text-white outline-none focus:border-amber-400/60 focus:ring-2 focus:ring-amber-400/20" />
        <div class="flex gap-2 mt-3">
          <div class="flex flex-col">
            <label class="text-[11px] font-bold uppercase tracking-[0.2em] text-gray-400 mb-1.5">Bots</label>
            <select id="bharat-bots" title="Raja bots in your room" class="rounded-xl border border-white/15 bg-black/30 px-3 py-2.5 text-sm text-white outline-none focus:border-amber-400/60">
              <option value="0">No bots</option>
              <option value="1" selected>1 bot</option>
              <option value="2">2 bots</option>
              <option value="3">3 bots</option>
              <option value="4">4 bots</option>
              <option value="5">5 bots</option>
            </select>
          </div>
          <div class="flex flex-col">
            <label class="text-[11px] font-bold uppercase tracking-[0.2em] text-gray-400 mb-1.5">Skill</label>
            <select id="bharat-diff" title="Bot skill" class="rounded-xl border border-white/15 bg-black/30 px-3 py-2.5 text-sm text-white outline-none focus:border-amber-400/60">
              <option value="easy">🌱 Easy</option>
              <option value="medium" selected>🧑 Medium</option>
              <option value="hard">🔥 Hard</option>
            </select>
          </div>
          <div class="flex flex-col">
            <label class="text-[11px] font-bold uppercase tracking-[0.2em] text-gray-400 mb-1.5">Resources</label>
            <select id="bharat-rich" title="Map resource abundance" class="rounded-xl border border-white/15 bg-black/30 px-3 py-2.5 text-sm text-white outline-none focus:border-amber-400/60">
              <option value="less">🦗 Less</option>
              <option value="normal" selected>🌾 Normal</option>
              <option value="large">🌳 Large</option>
              <option value="abundant">💰 Abundant</option>
            </select>
          </div>
          <div class="flex flex-col flex-1">
            <span class="text-[11px] font-bold uppercase tracking-[0.2em] text-transparent mb-1.5 select-none">·</span>
            <button id="bharat-create" class="flex-1 rounded-xl bg-gradient-to-b from-amber-400 to-orange-500 hover:from-amber-300 hover:to-orange-400 text-black font-black px-3 py-2.5 text-sm shadow-lg shadow-orange-950/50 active:scale-[0.98] transition">⚔️ Create room</button>
          </div>
        </div>
        <p id="bharat-diff-blurb" class="mt-1.5 text-[11px] text-gray-500">🧑 Medium — plays like a solid human.</p>
        <p id="bharat-rich-blurb" class="mt-1 text-[11px] text-gray-500">🌾 Normal — classic balance.</p>
      </div>
      <div class="mt-5 flex items-center justify-between">
        <h2 class="text-sm font-black text-white tracking-wide">Open rooms</h2>
        <button id="bharat-refresh" class="rounded-lg border border-white/10 bg-white/5 px-2.5 py-1 text-xs font-bold text-sky-300 hover:bg-white/10 transition">↻ Refresh</button>
      </div>
      <div id="bharat-rooms" class="mt-2 space-y-2 max-h-56 overflow-y-auto pr-1"></div>
      <p class="mt-4 text-xs text-gray-500 text-center">No rival online? Create a room with bots — the match starts instantly.</p>`;
    wrap.appendChild(card);
    L.appendChild(wrap);
    const nameInput = card.querySelector('#bharat-name') as HTMLInputElement;
    nameInput.value = this.cb.getName();
    nameInput.addEventListener('change', () => this.cb.setName(nameInput.value));
    card.querySelector('#bharat-create')!.addEventListener('click', () => {
      this.cb.setName(nameInput.value);
      const bots = parseInt((card.querySelector('#bharat-bots') as HTMLSelectElement).value, 10);
      const diff = (card.querySelector('#bharat-diff') as HTMLSelectElement).value;
      const rich = (card.querySelector('#bharat-rich') as HTMLSelectElement).value;
      this.cb.onCreate(bots, diff, rich);
    });
    const diffSel = card.querySelector('#bharat-diff') as HTMLSelectElement;
    const diffBlurb = card.querySelector('#bharat-diff-blurb') as HTMLElement;
    const syncBlurb = () => {
      const d = DIFFICULTY_INFO[diffSel.value] ?? DIFFICULTY_INFO.medium;
      diffBlurb.textContent = `${d.icon} ${d.name} — ${d.blurb}.`;
    };
    diffSel.addEventListener('change', syncBlurb);
    syncBlurb();
    const richSel = card.querySelector('#bharat-rich') as HTMLSelectElement;
    const richBlurb = card.querySelector('#bharat-rich-blurb') as HTMLElement;
    const syncRichBlurb = () => {
      const r = RICHNESS_INFO[richSel.value] ?? RICHNESS_INFO.normal;
      richBlurb.textContent = `${r.icon} ${r.name} — ${r.blurb}.`;
    };
    richSel.addEventListener('change', syncRichBlurb);
    syncRichBlurb();
    card.querySelector('#bharat-refresh')!.addEventListener('click', () => this.cb.onRefreshRooms());
    card.querySelector('#bharat-retry')?.addEventListener('click', () => this.cb.onRetry());
    this.renderRoomList(rooms);
  }

  renderRoomList(rooms: RoomInfo[]): void {
    const box = document.getElementById('bharat-rooms');
    if (!box) return;
    box.innerHTML = '';
    const open = rooms.filter((r) => !r.started);
    if (open.length === 0) {
      box.innerHTML = `
        <div class="rounded-xl border border-dashed border-white/15 px-4 py-6 text-center">
          <div class="text-2xl">🏜️</div>
          <p class="mt-1 text-sm text-gray-400">No open rooms — create one above!</p>
        </div>`;
      return;
    }
    for (const r of open) {
      const row = el(
        'div',
        'flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] hover:border-sky-400/40 hover:bg-white/[0.07] transition px-3 py-2.5 text-sm',
      );
      row.innerHTML = `
        <span class="rounded-lg border border-amber-300/20 bg-black/40 px-2 py-1 font-mono font-black tracking-[0.2em] text-amber-300">${esc(r.id)}</span>
        <span class="min-w-0 flex-1">
          <span class="block truncate text-gray-300">${r.players.map(esc).join(', ')}</span>
          <span class="mt-1 flex items-center gap-2">${seatDots(r.players.length, r.seats)}${r.difficulty ? `<span class="text-[10px] text-gray-500">${(DIFFICULTY_INFO[r.difficulty] ?? DIFFICULTY_INFO.medium).icon} ${(DIFFICULTY_INFO[r.difficulty] ?? DIFFICULTY_INFO.medium).name}</span>` : ''}${r.richness ? `<span class="text-[10px] text-gray-500">${(RICHNESS_INFO[r.richness] ?? RICHNESS_INFO.normal).icon} ${(RICHNESS_INFO[r.richness] ?? RICHNESS_INFO.normal).name}</span>` : ''}</span>
        </span>`;
      const btn = el(
        'button',
        'shrink-0 rounded-lg bg-gradient-to-b from-sky-500 to-sky-600 hover:from-sky-400 hover:to-sky-500 text-white text-xs font-black px-4 py-2 shadow active:scale-95 transition',
        'Join',
      );
      btn.addEventListener('click', () => {
        const inp = document.getElementById('bharat-name') as HTMLInputElement | null;
        if (inp) this.cb.setName(inp.value);
        this.cb.onJoin(r.id);
      });
      row.appendChild(btn);
      box.appendChild(row);
    }
  }

  showRoom(code: string, players: string[], seats: number, isOwner: boolean, started: boolean, difficulty = 'medium', richness = 'normal', bots: string[] = []): void {
    this.hide(this.top);
    this.hide(this.bottom);
    document.getElementById('mmwrap')?.classList.add('hidden');
    const L = this.lobby;
    L.innerHTML = '';
    this.show(L);
    const wrap = el('div', 'min-h-full flex items-center justify-center p-4 bg-[#070b18] relative overflow-hidden');
    wrap.innerHTML = `
      <div class="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-orange-600/15 blur-3xl pointer-events-none"></div>
      <div class="absolute -bottom-24 -left-24 w-80 h-80 rounded-full bg-indigo-600/15 blur-3xl pointer-events-none"></div>`;
    const card = el(
      'div',
      'relative w-full max-w-md rounded-2xl border border-amber-200/15 bg-[#0d1428]/90 shadow-[0_20px_80px_-15px_rgba(0,0,0,0.9)] backdrop-blur p-6 sm:p-8 text-gray-200 text-center my-6',
    );
    card.innerHTML = `
      <p class="text-[11px] font-bold uppercase tracking-[0.3em] text-gray-400">Room code — share it</p>
      <h1 class="mt-1 text-5xl font-black font-mono tracking-[0.2em] bg-gradient-to-b from-amber-100 to-orange-400 bg-clip-text text-transparent">${esc(code)}</h1>
      <button id="bharat-copy" class="mt-1.5 text-xs font-bold text-sky-300 hover:text-sky-200 transition">📋 Copy code</button>
      <div class="mt-2 flex justify-center" id="bharat-diff-row"></div>
      <div class="mt-3 mb-1">${seatDots(players.length, seats)}</div>
      <div id="bharat-seats" class="mt-2 space-y-1.5 text-sm text-left min-w-0"></div>`;
    const diffRow = card.querySelector('#bharat-diff-row') as HTMLElement;
    const dinfo = DIFFICULTY_INFO[difficulty] ?? DIFFICULTY_INFO.medium;
    const rinfo = RICHNESS_INFO[richness] ?? RICHNESS_INFO.normal;
    if (isOwner && !started) {
      diffRow.innerHTML = `
        <label class="text-[11px] font-bold uppercase tracking-[0.2em] text-gray-400 mr-2 self-center">Bot skill</label>
        <select id="bharat-diff-room" class="rounded-xl border border-white/15 bg-black/30 px-3 py-1.5 text-sm text-white outline-none focus:border-amber-400/60">
          ${['easy', 'medium', 'hard']
            .map(
              (d) =>
                `<option value="${d}"${d === difficulty ? ' selected' : ''}>${DIFFICULTY_INFO[d].icon} ${DIFFICULTY_INFO[d].name}</option>`,
            )
            .join('')}
        </select>
        <label class="text-[11px] font-bold uppercase tracking-[0.2em] text-gray-400 ml-3 mr-2 self-center">Riches</label>
        <select id="bharat-rich-room" class="rounded-xl border border-white/15 bg-black/30 px-3 py-1.5 text-sm text-white outline-none focus:border-amber-400/60">
          ${['less', 'normal', 'large', 'abundant']
            .map(
              (r) =>
                `<option value="${r}"${r === richness ? ' selected' : ''}>${RICHNESS_INFO[r].icon} ${RICHNESS_INFO[r].name}</option>`,
            )
            .join('')}
        </select>`;
      (diffRow.querySelector('#bharat-diff-room') as HTMLSelectElement).addEventListener('change', (e) => {
        this.cb.onSetDiff((e.target as HTMLSelectElement).value);
      });
      (diffRow.querySelector('#bharat-rich-room') as HTMLSelectElement).addEventListener('change', (e) => {
        this.cb.onSetRich((e.target as HTMLSelectElement).value);
      });
    } else {
      diffRow.innerHTML = `<span class="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] text-gray-300">${dinfo.icon} Bots: ${dinfo.name}</span><span class="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] text-gray-300 ml-2">${rinfo.icon} ${rinfo.name}</span>`;
    }
    const copyBtn = card.querySelector('#bharat-copy') as HTMLButtonElement;
    copyBtn.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(code);
        copyBtn.textContent = '✅ Copied!';
      } catch {
        copyBtn.textContent = '⚠️ Copy failed — select the code';
      }
      setTimeout(() => {
        copyBtn.textContent = '📋 Copy code';
      }, 1600);
    });
    const list = card.querySelector('#bharat-seats') as HTMLElement;
    const botSet = new Set(bots);
    players.forEach((p) => {
      const bot = botSet.has(p);
      list.appendChild(
        el(
          'div',
          `flex items-center gap-2 rounded-xl border px-3 py-2 text-sm ${bot ? 'border-purple-400/20 bg-purple-400/5' : 'border-emerald-400/20 bg-emerald-400/5'}`,
          `<span class="shrink-0">${bot ? '🤖' : '🧑'}</span><span class="flex-1 min-w-0 text-left truncate text-gray-200">${esc(p)}</span><span class="shrink-0 text-[10px] font-bold uppercase tracking-wide ${bot ? 'text-purple-300' : 'text-emerald-300'}">${bot ? 'bot' : 'human'}</span>`,
        ),
      );
    });
    for (let i = players.length; i < seats; i++) {
      list.appendChild(
        el(
          'div',
          'rounded-xl border border-dashed border-white/15 px-3 py-2 text-gray-600 text-center text-xs',
          '— waiting for warriors —',
        ),
      );
    }
    const btns = el('div', 'flex flex-wrap gap-2 justify-center mt-5');
    if (!started) {
      if (isOwner) {
        const addBot = el('button', 'cmd-btn', '🤖<span>Add bot</span>');
        addBot.addEventListener('click', () => this.cb.onAddBot());
        const start = el(
          'button',
          'rounded-xl bg-gradient-to-b from-amber-400 to-orange-500 hover:from-amber-300 hover:to-orange-400 text-black font-black px-6 py-2.5 text-sm shadow-lg shadow-orange-950/50 active:scale-[0.98] transition disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100',
          '⚔️ Start battle',
        );
        if (players.length < 2) {
          start.setAttribute('disabled', '');
          btns.appendChild(el('p', 'w-full text-xs text-gray-400', 'Need 2+ to start — add a bot 🤖 or wait for a friend'));
        } else {
          start.addEventListener('click', () => this.cb.onStart());
        }
        btns.append(addBot, start);
      } else {
        btns.appendChild(
          el('p', 'w-full rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-gray-400', 'Waiting for the host to start… sharpen your swords. ⚔️'),
        );
      }
    } else {
      btns.appendChild(el('p', 'w-full text-sm text-gray-400', 'Battle in progress…'));
    }
    const leave = el(
      'button',
      'rounded-xl border border-white/10 px-4 py-2 text-xs font-bold text-gray-400 hover:bg-white/5 hover:text-gray-200 transition',
      '🚪 Leave',
    );
    leave.addEventListener('click', () => this.cb.onLeave());
    btns.appendChild(leave);
    card.appendChild(btns);
    wrap.appendChild(card);
    L.appendChild(wrap);
  }

  // ---- in-game ----

  enterGame(roomCode: string, isOwner: boolean): void {
    this.hide(this.lobby);
    this.hide(this.over);
    this.top.classList.remove('hidden');
    this.top.classList.add('flex');
    this.bottom.classList.remove('hidden');
    document.getElementById('mmwrap')?.classList.remove('hidden');
    document.getElementById('mmwrap')?.classList.add('flex');
    void isOwner;
    void roomCode;
  }

  setTop(me: PlayerState | null, others: PlayerState[], roomCode: string): void {
    if (!me) return;
    const dot = (p: PlayerState) =>
      `<span title="${p.n}${p.b ? ' (bot)' : ''}" class="inline-block w-2.5 h-2.5 rounded-full ${p.a ? '' : 'opacity-30'}" style="background:${p.a ? '#4ade80' : '#666'}"></span> ${p.n}${p.b ? '🤖' : ''}`;
    // Guarded like the command row: rebuilding ?/leave under the cursor
    // drops hover for a frame (flicker), so only swap on real change.
    const html = `
      <span title="Wood">🪵 <b id="r-w">${Math.floor(me.w)}</b></span>
      <span title="Gold">💰 <b id="r-g">${Math.floor(me.g)}</b></span>
      <span title="Food">🍎 <b id="r-f">${Math.floor(me.f)}</b></span>
      <span title="Population">🧑 <b>${me.p}/${me.c}</b></span>
      <span class="text-gray-500">|</span>
      <span class="text-xs text-gray-300">${others.map(dot).join(' · ')}</span>
      <span class="text-gray-500">|</span>
      <span class="font-mono text-amber-300 text-xs">${roomCode}</span>
      <button data-act="help" class="text-xs text-sky-300 hover:underline">?</button>
      <a href="/?feedback=1&game=bharat" target="_blank" rel="noreferrer" class="text-xs text-sky-300 hover:underline" title="Send feedback about Cities of Bharat">💬</a>
      <button data-act="leave" title="Leave match — a bot takes over your city so others can continue" class="rounded-lg border border-red-400/40 bg-red-400/10 px-2 py-0.5 text-xs font-bold text-red-200 hover:bg-red-400/20 transition">🏳️ Leave</button>`;
    if (html !== this.lastTopHTML) {
      this.lastTopHTML = html;
      this.top.innerHTML = html;
    }
  }

  setPanel(ctx: PanelCtx): void {
    // Info text is button-free: safe to rewrite every tick.
    const info = el('div', '');
    // Command row builds into a scratch node; swapped in only when the
    // definition changed (hovered buttons are never replaced by ticks).
    const cmds = el('div', '');
    const sink: LiveCost[] = [];

    if (ctx.enemy) {
      const e = ctx.enemy;
      const nm = 'k' in e && e.k in UNIT_INFO ? UNIT_INFO[e.k].name : BLD_INFO[(e as Building).k]?.name ?? 'Enemy';
      const eb = e as Building;
      const ebLvl = Math.min(eb.lv ?? 0, MAX_WALL_LVL);
      const eLvl = !('k' in e && e.k in UNIT_INFO) && eb.k === 'tower'
        ? ` · ${TOWER_LEVELS[eb.lv ?? 0]?.icon ?? ''} ${TOWER_LEVELS[eb.lv ?? 0]?.name ?? ''}`
        : !('k' in e && e.k in UNIT_INFO) && eb.k === 'wall'
          ? ` · ${WALL_LEVELS[ebLvl]?.icon ?? ''} ${WALL_LEVELS[ebLvl]?.name ?? ''}`
          : !('k' in e && e.k in UNIT_INFO) && eb.k === 'gate'
            ? ` · 🛡️ ${GATE_LEVEL_NAMES[ebLvl] ?? ''}`
            : '';
      info.innerHTML = `<b class="text-red-300">Enemy ${nm}</b>${eLvl} · HP ${Math.ceil(e.hp)}/${e.m}`;
    } else if (ctx.bld) {
      const b = ctx.bld;
      const meta = BLD_INFO[b.k];
      const q = b.q ?? [];
      const crown = b.k === 'townCenter' ? ' 👑 capital — lose it = defeat' : '';
      const lvl = b.k === 'tower' ? Math.min(b.lv ?? 0, MAX_TOWER_LVL) : 0;
      const tier = TOWER_LEVELS[lvl];
      const towerTag = b.k === 'tower' ? ` · ${tier.icon} ${tier.name} (${tier.weapon} ${tier.dmg}dmg)` : '';
      const blvl = b.k === 'barracks' ? Math.min(b.lv ?? 0, MAX_BARRACKS_LVL) : 0;
      const btier = BARRACKS_LEVELS[blvl];
      const barracksTag = b.k === 'barracks' && blvl > 0 ? ` · ${btier.icon} ${btier.name}` : '';
      const wlvl = (b.k === 'wall' || b.k === 'gate') ? Math.min(b.lv ?? 0, MAX_WALL_LVL) : 0;
      const wtier = WALL_LEVELS[wlvl];
      const wallTag = b.k === 'wall' ? ` · ${wtier.icon} ${wtier.name}` : '';
      const gateTag = b.k === 'gate' ? ` · 🛡️ ${GATE_LEVEL_NAMES[wlvl] ?? ''} (auto-door)` : '';
      const researching = (b.ut ?? 0) > 0;
      const nextUpName = b.k === 'tower' && lvl < MAX_TOWER_LVL ? TOWER_LEVELS[lvl + 1].name
        : b.k === 'barracks' && blvl < MAX_BARRACKS_LVL ? BARRACKS_LEVELS[blvl + 1].name
        : b.k === 'wall' && wlvl < MAX_WALL_LVL ? WALL_LEVELS[wlvl + 1].name
        : b.k === 'gate' && wlvl < MAX_WALL_LVL ? (GATE_LEVEL_NAMES[wlvl + 1] ?? '') : '';
      info.innerHTML = `<b>${meta?.icon ?? ''} ${meta?.name ?? b.k}</b>${crown}${towerTag}${barracksTag}${wallTag}${gateTag} · HP ${Math.ceil(b.hp)}/${b.m}` +
        (b.p < 1 ? ` · 🚧 ${Math.floor(b.p * 100)}% — right-click with 🧑‍🌾 to resume` : '') +
        (researching && nextUpName ? ` · ⬆️ ${nextUpName} ${Math.floor(Math.min(b.ut ?? 0, 1) * 100)}%` : '') +
        (b.p >= 1 && b.hp < b.m ? ` · 🛠️ right-click with 🧑‍🌾 to repair` : '') +
        (q.length ? ` · queue: ${q.map((k) => UNIT_INFO[k]?.icon ?? k).join(' ')}` : '');
      if (b.p >= 1) {
        if (b.k === 'townCenter') {
          cmds.appendChild(this.trainBtn('villager', '50🍎', '1', ctx));
        } else if (b.k === 'barracks') {
          cmds.appendChild(this.trainBtn('swordsman', '60🍎 25💰', '1', ctx));
          cmds.appendChild(this.trainBtn('archer', '25🍎 45🪵 40💰', '2', ctx));
          if (blvl >= 1) {
            cmds.appendChild(this.trainBtn('spearman', '50🍎 20🪵', '3', ctx));
          } else {
            cmds.appendChild(this.lockedBtn('🔱', 'Spearman', 'Armory L1'));
          }
          if (blvl >= 2) {
            cmds.appendChild(this.trainBtn('rajput', '90🍎 60💰', '4', ctx));
          } else {
            cmds.appendChild(this.lockedBtn('🛡️', 'Rajput', 'Akhara L2'));
          }
          if (blvl < MAX_BARRACKS_LVL && !researching) {
            const next = BARRACKS_LEVELS[blvl + 1];
            this.affordBtn(cmds, sink, 'cmd-btn', 'upgrade', '',
              next.icon, next.name, `${next.cost} [U] → ${next.unlockName} +${next.hp}HP`,
              { w: next.w, g: next.g, f: 0 });
          } else if (researching) {
            cmds.appendChild(this.lockedBtn('⬆️', nextUpName, 'researching…', '⏳'));
          } else {
            info.innerHTML += `<br><span class="text-amber-200">${btier.icon} MAX — all elites unlocked</span>`;
          }
        } else if (b.k === 'tower') {
          if (lvl < MAX_TOWER_LVL && !researching) {
            const next = TOWER_LEVELS[lvl + 1];
            this.affordBtn(cmds, sink, 'cmd-btn', 'upgrade', '',
              next.icon, next.name, `${next.cost} [U] → ${next.weapon} ${next.dmg}dmg +${next.hp}HP`,
              { w: next.w, g: next.g, f: 0 });
          } else if (researching) {
            cmds.appendChild(this.lockedBtn('⬆️', nextUpName, 'researching…', '⏳'));
          } else {
            info.innerHTML += `<br><span class="text-amber-200">${tier.icon} MAX — ${tier.weapon} ${tier.dmg}dmg, range ${tier.range}</span>`;
          }
        } else if (b.k === 'wall') {
          if (wlvl < MAX_WALL_LVL && !researching) {
            const next = WALL_LEVELS[wlvl + 1];
            this.affordBtn(cmds, sink, 'cmd-btn', 'upgrade', '',
              next.icon, next.name, `${next.cost} [U] → stone +${next.hp}HP`,
              { w: next.w, g: next.g, f: 0 });
          } else if (researching) {
            cmds.appendChild(this.lockedBtn('⬆️', nextUpName, 'researching…', '⏳'));
          } else {
            info.innerHTML += `<br><span class="text-amber-200">${wtier.icon} MAX — stone fortification</span>`;
          }
          // Swing this segment into a gate: friendlies stroll through while
          // enemies face a sealed footprint. Instant, keeps tier + health.
          if (!researching) {
            this.affordBtn(cmds, sink, 'cmd-btn', 'convert', '',
              '🛡️', 'Gate', '30🪵 [G] → auto-door for friendlies',
              { w: 30, g: 0, f: 0 });
          }
        } else if (b.k === 'gate') {
          if (wlvl < MAX_WALL_LVL && !researching) {
            const next = WALL_LEVELS[wlvl + 1];
            this.affordBtn(cmds, sink, 'cmd-btn', 'upgrade', '',
              '🛡️', GATE_LEVEL_NAMES[wlvl + 1] ?? next.name, `${next.cost} [U] → stone +${next.hp}HP`,
              { w: next.w, g: next.g, f: 0 });
          } else if (researching) {
            cmds.appendChild(this.lockedBtn('⬆️', nextUpName, 'researching…', '⏳'));
          } else {
            info.innerHTML += `<br><span class="text-amber-200">🛡️ ${GATE_LEVEL_NAMES[wlvl] ?? ''} MAX — stone gatehouse</span>`;
          }
        }
      }
    } else if (ctx.units.length > 0) {
      // Live activity: one detailed line for a lone unit, grouped chips
      // for a selection (e.g. "🪵 Chopping wood ×2 · 🔨 Building ×1").
      let act: string;
      if (ctx.units.length === 1) {
        act = `<span class="text-amber-200">${this.unitActivity(ctx.units[0], ctx.snap)}</span>`;
      } else {
        const groups = new Map<string, number>();
        for (const u of ctx.units) {
          const a = this.unitActivity(u, ctx.snap);
          groups.set(a, (groups.get(a) ?? 0) + 1);
        }
        act = [...groups.entries()].map(([a, n]) => `${a} ×${n}`).join(' · ');
      }
      if (ctx.units.length === 1) {
        const u = ctx.units[0];
        const frac = u.m > 0 ? u.hp / u.m : 0;
        info.innerHTML = `${UNIT_INFO[u.k]?.icon ?? ''} ${UNIT_INFO[u.k]?.name ?? u.k}` +
          ` · HP ${Math.max(0, Math.ceil(u.hp))}/${u.m}${this.hpBar(frac, false)}` +
          `<br><span class="text-gray-400">${act}</span>`;
      } else {
        const byKind = new Map<string, Unit[]>();
        for (const u of ctx.units) {
          const list = byKind.get(u.k) ?? [];
          list.push(u);
          byKind.set(u.k, list);
        }
        const rows = [...byKind.entries()].map(([k, list]) => {
          const m = list[0].m;
          const avg = list.reduce((t, u) => t + Math.max(u.hp, 0), 0) / list.length;
          const frac = m > 0 ? avg / m : 0;
          return `${UNIT_INFO[k]?.icon ?? ''} ${list.length}× ${UNIT_INFO[k]?.name ?? k}` +
            ` · ${Math.round(avg)}/${m}${this.hpBar(frac, true)}`;
        }).join('<br>');
        info.innerHTML = `${rows}<br><span class="text-gray-400">${act}</span>`;
      }
      const hasVill = ctx.units.some((u) => u.k === 'villager');
      const hasArmy = ctx.units.some((u) => u.k !== 'villager');
      if (hasVill) {
        for (const kind of ['house', 'farm', 'barracks', 'tower', 'wall']) {
          const m = BLD_INFO[kind];
          // Unmissable affordability: the button itself shows what's missing
          // (the tower is the only gold-cost building, the usual surprise).
          // Amounts refresh in place (see refreshLiveCosts) so hovering
          // never rebuilds the button.
          const cost = BLD_COSTS[kind];
          this.affordBtn(cmds, sink, 'cmd-btn' + (ctx.placement === kind ? ' armed' : ''),
            'place', ctx.placement === kind ? '' : kind,
            m.icon, m.name.split(' ')[0], `${m.cost} [${m.hotkey}]`, cost);
        }
      }
      if (hasArmy || hasVill) {
        cmds.append(
          this.actBtn('cmd-btn' + (ctx.amoveArmed ? ' armed' : ''), 'amove', '', `🎯<span>Attack [A]</span>`),
          this.actBtn('cmd-btn', 'stop', '', `✋<span>Stop [S]</span>`),
        );
      }
    } else if (ctx.node) {
      const n = ctx.node;
      const meta = n.k === 'wood'
        ? { icon: '🌳', name: 'Timber Tree', unit: '🪵' }
        : n.k === 'gold'
          ? { icon: '⛏️', name: 'Gold Mine', unit: '💰' }
          : { icon: '🫐', name: 'Berry Bush', unit: '🍎' };
      info.innerHTML = `<b>${meta.icon} ${meta.name}</b> · ${Math.max(0, Math.ceil(n.a))}${meta.unit} left · 3/s per villager`;
      cmds.appendChild(this.actBtn('cmd-btn', 'gather-node', '',
        `🧑‍🌾<span>Gather</span><span class="cost">nearest idle villagers</span>`));
    } else {
      info.innerHTML = '<span class="text-gray-400">Select villagers & soldiers — drag for a box, double-click for all of a type · click trees, mines & bushes to inspect them</span>';
    }
    // Info text has no buttons: always safe to refresh.
    this.barInfo.innerHTML = info.innerHTML;
    // Command row swaps only when its definition changed — a hovered button
    // is never replaced by a timer tick (no flicker). Ticking affordability
    // amounts refresh in place below.
    const cmdHTML = cmds.innerHTML;
    if (cmdHTML !== this.lastCmdHTML) {
      this.lastCmdHTML = cmdHTML;
      this.barCmds.replaceChildren(...Array.from(cmds.childNodes));
      this.liveCosts = sink;
    }
    this.refreshLiveCosts(ctx.me);

    // Right cell: same guard — counts change discretely, never 4Hz churn.
    const R = this.barRight;
    if (ctx.overview) {
      const ov = ctx.overview;
      const tmp = el('div', '');
      const qbtn = (label: string, title: string, act: string, arg: string) =>
        this.actBtn('ov-btn', act, arg, label, false, title);
      const vrow = el('div', 'flex gap-1 justify-start items-center flex-wrap text-xs text-gray-200');
      vrow.appendChild(el('span', 'aoe-eyebrow', 'VILLAGE'));
      vrow.appendChild(el('span', '',
        `🧑‍🌾 <b>${ov.villagers}</b>` +
        (ov.idle > 0 ? ` · <span class="text-amber-200">${ov.idle} idle</span>` : '')));
      vrow.appendChild(qbtn('All', 'Select all villagers', 'sel-vill', 'all'));
      vrow.appendChild(qbtn('Idle', 'Select idle villagers (.)', 'sel-vill', 'idle'));
      vrow.appendChild(qbtn('+5', 'Select 5 villagers nearest the camera', 'sel-vill', 'few'));
      const arow = el('div', 'flex gap-1 justify-start items-center flex-wrap text-xs text-gray-200');
      if (ov.armyTotal > 0) {
        arow.appendChild(el('span', 'aoe-eyebrow', 'ARMY'));
        arow.appendChild(el('span', '', `⚔️ <b>${ov.armyTotal}</b>`));
        arow.appendChild(qbtn('Army', 'Select all soldiers', 'sel-army', 'all'));
        for (const a of ov.army) {
          arow.appendChild(qbtn(
            `${UNIT_INFO[a.kind]?.icon ?? ''} ${a.n}`,
            `Select ${UNIT_INFO[a.kind]?.name ?? a.kind}`,
            'sel-army', a.kind,
          ));
        }
      } else {
        arow.appendChild(el('span', 'text-gray-400', '⚔️ 0 — train soldiers at the Barracks'));
      }
      tmp.append(vrow, arow);
      const ovHTML = tmp.innerHTML;
      if (ovHTML !== this.lastOvHTML) {
        this.lastOvHTML = ovHTML;
        R.replaceChildren(...Array.from(tmp.childNodes));
      }
    } else if (this.lastOvHTML !== '') {
      this.lastOvHTML = '';
      R.innerHTML = '';
    }

    if (ctx.placement) {
      const m = BLD_INFO[ctx.placement];
      this.hint.innerHTML = ctx.placement === 'wall'
        ? `Placing <b>${m?.name}</b> — click-drag to lay a rampart line (up to 16), release to build · shift keeps placing · right-click / Esc cancels`
        : `Placing <b>${m?.name}</b> — left-click to build, right-click / Esc to cancel`;
      this.show(this.hint);
    } else if (ctx.amoveArmed) {
      this.hint.innerHTML = `Attack-move armed — click a target. Right-click / Esc to cancel`;
      this.show(this.hint);
    } else {
      this.hide(this.hint);
    }
  }

  // HP bar snippet for the selection card (green → yellow → red).
  private hpBar(frac: number, mini: boolean): string {
    const c = Math.min(Math.max(frac, 0), 1);
    const col = c > 0.6 ? '#4ade80' : c > 0.3 ? '#fbbf24' : '#ef4444';
    return `<span class="${mini ? 'hp-mini' : 'hp-bar'}"><span class="hp-fill" style="width:${(c * 100).toFixed(1)}%;background:${col}"></span></span>`;
  }

  // What a selected unit is doing right now, in plain words.
  private unitActivity(u: Unit, snap: Snapshot | null): string {
    const load = u.cn && u.cn > 0 ? ` (${Math.floor(u.cn)}/15)` : '';
    switch (u.st) {
      case 'gather': {
        if (u.tb) return `🌾 Farming${load}`;
        const kind = snap?.nd.find((n) => n.id === u.tid)?.k ?? u.ca;
        if (kind === 'wood') return `🪵 Chopping wood${load}`;
        if (kind === 'gold') return `💰 Mining gold${load}`;
        return `🍎 Gathering food${load}`;
      }
      case 'ret': {
        const what = u.ca === 'wood' ? 'wood 🪵' : u.ca === 'gold' ? 'gold 💰' : 'food 🍎';
        return `🚶 Hauling ${what} home`;
      }
      case 'build': {
        const b = snap?.bd.find((x) => x.id === u.tid);
        const nm = (b && BLD_INFO[b.k]?.name) ?? 'building';
        if (b && b.p >= 1) return `🛠️ Repairing ${nm}`;
        const pct = b ? ` ${Math.floor(b.p * 100)}%` : '';
        return `🔨 Building ${nm}${pct}`;
      }
      case 'attack':
        return '⚔️ Attacking';
      case 'amove':
        return '🎯 Attack-moving';
      case 'move':
        return '➡️ Moving';
      default:
        return '💤 Idle';
    }
  }

  private trainBtn(kind: string, cost: string, hotkey: string, ctx: PanelCtx): HTMLElement {
    const me = ctx.me;
    const can = !!me && me.p < me.c;
    return this.actBtn('cmd-btn', 'train', kind,
      `${UNIT_INFO[kind].icon}<span>${UNIT_INFO[kind].name} [${hotkey}]</span><span class="cost">${cost}</span>`, !can);
  }

  // Routed button: no own listener (see the delegated router in the
  // constructor) — survives the 4Hz panel re-renders.
  private actBtn(cls: string, act: string, arg: string, html: string, disabled = false, title = ''): HTMLElement {
    const btn = el('button', cls, html);
    btn.setAttribute('data-act', act);
    btn.setAttribute('data-arg', arg);
    if (title) btn.title = title;
    if (disabled) btn.setAttribute('disabled', '');
    return btn;
  }

  // Affordability button: the shell (and its .cost span) is built once per
  // command-row definition; ticking amounts + poor state refresh in place
  // so a hovered button is never swapped out from under the cursor.
  private affordBtn(parent: HTMLElement, sink: LiveCost[], cls: string, act: string, arg: string,
    icon: string, name: string, okLabel: string, needs: { w: number; g: number; f: number }): void {
    const btn = this.actBtn(cls, act, arg, `${icon}<span>${name}</span><span class="cost"></span>`);
    const cost = btn.querySelector('.cost') as HTMLElement;
    parent.appendChild(btn);
    sink.push({ btn, cost, needs, okLabel });
  }

  private refreshLiveCosts(me: PlayerState | null): void {
    for (const e of this.liveCosts) {
      const lacking: string[] = [];
      if (me) {
        if (me.w < e.needs.w) lacking.push(`${Math.ceil(e.needs.w - me.w)}🪵`);
        if (me.g < e.needs.g) lacking.push(`${Math.ceil(e.needs.g - me.g)}💰`);
        if (me.f < e.needs.f) lacking.push(`${Math.ceil(e.needs.f - me.f)}🍎`);
      }
      e.cost.textContent = lacking.length > 0 ? `Need ${lacking.join(' ')}` : e.okLabel;
      e.btn.classList.toggle('poor', lacking.length > 0);
    }
  }

  // Greyed chip for a unit the barracks tier hasn't unlocked yet (or an
  // upgrade already researching — static text keeps the row hover-stable).
  private lockedBtn(icon: string, name: string, need: string, lockIcon = '🔒'): HTMLElement {
    const btn = el('button', 'cmd-btn poor',
      `${icon}<span>${name}</span><span class="cost">${lockIcon} ${need}</span>`);
    btn.setAttribute('disabled', '');
    return btn;
  }

  toast(msg: string): void {
    const now = Date.now();
    if (now - this.lastToast < 300) return;
    this.lastToast = now;
    const t = el('div', 'hud-panel px-3 py-1 text-xs text-amber-100', msg);
    this.toasts.appendChild(t);
    while (this.toasts.children.length > 3) this.toasts.firstChild?.remove();
    setTimeout(() => t.remove(), 3500);
  }

  clearToasts(): void {
    this.toasts.innerHTML = '';
  }

  gameOver(won: boolean, draw: boolean, snap: Snapshot | null, myPid: number, winner: number): void {
    this.hide(this.hint);
    this.over.innerHTML = '';
    this.show(this.over, true);
    const card = el('div', 'hud-panel p-6 sm:p-8 text-center max-w-2xl w-[min(92vw,42rem)] max-h-[88vh] overflow-y-auto');
    const title = draw
      ? `<h1 class="text-3xl font-black text-gray-300">🤝 Draw</h1><p class="text-gray-400 mt-1">Mutual annihilation!</p>`
      : won
        ? `<h1 class="text-3xl font-black text-amber-300">🏆 Victory!</h1><p class="text-gray-400 mt-1">Your city rules Bharat.</p>`
        : `<h1 class="text-3xl font-black text-red-400">💀 Defeat</h1><p class="text-gray-400 mt-1">Your city has fallen.</p>`;
    card.innerHTML = title;
    if (snap && snap.pl.length > 0) {
      card.appendChild(this.leaderboard(snap, myPid, winner));
    }
    const btn = el('button', 'mt-5 rounded-xl bg-gradient-to-b from-amber-400 to-orange-500 hover:from-amber-300 hover:to-orange-400 text-black font-black px-6 py-2.5 text-sm shadow-lg shadow-orange-950/50 active:scale-[0.98] transition', 'Back to lobby');
    btn.addEventListener('click', () => this.cb.onLeave());
    card.appendChild(btn);
    this.over.appendChild(card);
  }

  // End-screen leaderboard: winner first, then the living, then by kills
  // and lifetime gathered. Counts come from the final snapshot.
  private leaderboard(snap: Snapshot, myPid: number, winner: number): HTMLElement {
    const stats = new Map((snap.st ?? []).map((s) => [s.id, s]));
    const vills = new Map<number, number>();
    const army = new Map<number, number>();
    for (const u of snap.u) {
      const m = u.k === 'villager' ? vills : army;
      m.set(u.o, (m.get(u.o) ?? 0) + 1);
    }
    const blds = new Map<number, number>();
    for (const b of snap.bd) blds.set(b.o, (blds.get(b.o) ?? 0) + 1);
    const gathered = (id: number) => {
      const s = stats.get(id);
      return s ? s.w + s.g + s.f : 0;
    };
    const ordered = [...snap.pl].sort((a, b) => {
      if (a.id === winner && b.id !== winner) return -1;
      if (b.id === winner && a.id !== winner) return 1;
      if (a.a !== b.a) return a.a ? -1 : 1;
      const ka = stats.get(a.id)?.k ?? 0;
      const kb = stats.get(b.id)?.k ?? 0;
      if (ka !== kb) return kb - ka;
      return gathered(b.id) - gathered(a.id);
    });
    const table = el('div', 'mt-4 overflow-x-auto');
    const medals = ['🥇', '🥈', '🥉'];
    let html = `<table class="w-full text-xs sm:text-sm border-collapse">
      <thead><tr class="text-gray-400 uppercase text-[10px] tracking-wider">
        <th class="px-2 py-1.5 text-left">#</th>
        <th class="px-2 py-1.5 text-left">Player</th>
        <th class="px-2 py-1.5 text-right" title="Villagers">🧑‍🌾</th>
        <th class="px-2 py-1.5 text-right" title="Army">⚔️</th>
        <th class="px-2 py-1.5 text-right" title="Buildings">🏠</th>
        <th class="px-2 py-1.5 text-right" title="Lifetime gathered">📦</th>
        <th class="px-2 py-1.5 text-right" title="Units trained">🎓</th>
        <th class="px-2 py-1.5 text-right" title="Kills">💀</th>
      </tr></thead><tbody>`;
    ordered.forEach((p, i) => {
      const s = stats.get(p.id);
      const isMe = p.id === myPid;
      const isWin = p.id === winner;
      const status = isWin ? ' 👑' : p.a ? '' : ' ☠️';
      html += `<tr class="border-t border-white/10 ${isMe ? 'bg-amber-400/10' : ''}">
        <td class="px-2 py-1.5 text-left">${medals[i] ?? `${i + 1}`}</td>
        <td class="px-2 py-1.5 text-left max-w-[10rem]"><span class="block truncate font-bold ${isWin ? 'text-amber-300' : 'text-gray-100'}">${esc(p.n)}${status}${isMe ? ' (you)' : ''}</span>
          <span class="block text-[10px] text-gray-500">${p.b ? '🤖 bot' : '🧑 human'} · ${p.a ? 'standing' : 'eliminated'}</span></td>
        <td class="px-2 py-1.5 text-right text-gray-200">${vills.get(p.id) ?? 0}</td>
        <td class="px-2 py-1.5 text-right text-gray-200">${army.get(p.id) ?? 0}</td>
        <td class="px-2 py-1.5 text-right text-gray-200">${blds.get(p.id) ?? 0}</td>
        <td class="px-2 py-1.5 text-right text-gray-200">${Math.floor(gathered(p.id))}</td>
        <td class="px-2 py-1.5 text-right text-gray-200">${s?.tr ?? 0}</td>
        <td class="px-2 py-1.5 text-right text-gray-200">${s?.k ?? 0}</td>
      </tr>`;
    });
    html += '</tbody></table>';
    table.innerHTML = html;
    return table;
  }
}
